import MainStore, { uniquePrimitives } from "./MainStore";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useReducer, useRef, useState } from "react";
import InfiniteCanvas from "./InfiniteCanvas";
import BoardViewer, { IGNORE_NODES_FOR_EXPORT } from "./BoardViewer";
import { createPptx, exportKonvaToPptx } from "./PptHelper";
import Konva from "konva";
import CustomImage from "./CustomImage";
import { renderDatatable, renderPlainObject, renderProcessingPlaceholder, themes } from "./RenderHelpers";
import useDataEvent from "./CustomHook";
import { buildViewRenderInput } from "./utils/viewRendering";
import { buildCanvasClickHandlers } from "./utils/canvasClickHandlers";
import { OUTPUT_ENTITY_TYPES, groupPrimitivesByType } from "./utils/flowEvidence";

const FLOW_ACTIVE_STATUSES = new Set(["running", "pending", "queued"]);
const SENSE_LOGO_URL = "/images/logo.png"
const CO_CREATED_LOGO_URL = "/api/companyLogo?domain=co-created.com"
const IMAGE_DATA_CACHE = new Map()

function getActiveLayoutInches(pptx){
    const layout = pptx?.LAYOUTS?.[pptx.layout]
    if( !layout ){
        return { width: 10, height: 5.625 }
    }
    return {
        width: layout.width / 914400,
        height: layout.height / 914400,
    }
}

function formatCoverDate(value = new Date()){
    try{
        return new Intl.DateTimeFormat("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric"
        }).format(value)
    }catch{
        return `${value}`
    }
}

function resolveExportUserName(mainstore){
    return mainstore?.activeUser?.info?.name
        ?? mainstore?.activeUser?.name
        ?? mainstore?.activeUser?.info?.email
        ?? mainstore?.activeUser?.email
        ?? "Unknown user"
}

function formatMetricNumber(value){
    if( value == null || Number.isNaN(Number(value)) ){
        return "0"
    }
    return new Intl.NumberFormat("en-US").format(Number(value))
}

function getProgressSearchCount(value, depth = 0) {
    if (depth > 3 || value == null) {
        return 0
    }
    if (typeof value === "object") {
        const candidates = [
            value.search_query_count,
            value.searchQueryCount,
            value.query_count,
            value.queryCount,
        ]
        for (const candidate of candidates) {
            const parsed = Number(candidate)
            if (Number.isFinite(parsed) && parsed >= 0) {
                return parsed
            }
        }
    }
    return 0
}

function getProgressUrlExaminedCount(value, depth = 0) {
    if (depth > 3 || value == null) {
        return 0
    }
    if (typeof value === "object") {
        const candidates = [
            value.url_examined_count,
            value.urlExaminedCount,
            value.urls_examined_count,
            value.urlsExaminedCount,
            value.urls_examined,
            value.totalScanned,
            value.scanned,
        ]
        for (const candidate of candidates) {
            const parsed = Number(candidate)
            if (Number.isFinite(parsed) && parsed >= 0) {
                return parsed
            }
        }
    }
    return 0
}

function resolvePrimitiveSearchQueryCount(primitive) {
    let best = 0
    if (primitive?.processing && typeof primitive.processing === "object") {
        Object.values(primitive.processing).forEach((entry) => {
            if (!entry || typeof entry !== "object") {
                return
            }
            best = Math.max(best, getProgressSearchCount(entry, 1))
            if (entry.progress && typeof entry.progress === "object") {
                best = Math.max(best, getProgressSearchCount(entry.progress, 2))
            }
        })
    }
    const statsCount = Number(
        primitive?.progressStats?.search_query_count ??
        primitive?.progressStats?.searchQueryCount ??
        primitive?.progressStats?.query_count ??
        primitive?.progressStats?.queryCount,
    )
    if (Number.isFinite(statsCount) && statsCount > 0) {
        best = Math.max(best, statsCount)
    }
    const scannedFallback = Number(
        primitive?.progressStats?.totalScanned ??
        primitive?.processing?.query?.totalScanned ??
        primitive?.processing?.query?.scanned,
    )
    if (
        best === 0 &&
        primitive?.type === "search" &&
        Number.isFinite(scannedFallback) &&
        scannedFallback > 0
    ) {
        best = 1
    }
    return best
}

function resolvePrimitiveUrlExaminedCount(primitive) {
    let best = 0
    if (primitive?.processing && typeof primitive.processing === "object") {
        Object.values(primitive.processing).forEach((entry) => {
            if (!entry || typeof entry !== "object") {
                return
            }
            best = Math.max(best, getProgressUrlExaminedCount(entry, 1))
            if (entry.progress && typeof entry.progress === "object") {
                best = Math.max(best, getProgressUrlExaminedCount(entry.progress, 2))
            }
        })
    }
    const scannedCandidates = [
        primitive?.progressStats?.totalScanned,
        primitive?.progressStats?.scanned,
        primitive?.processing?.query?.totalScanned,
        primitive?.processing?.query?.scanned,
    ]
    scannedCandidates.forEach((candidate) => {
        const parsed = Number(candidate)
        if (Number.isFinite(parsed) && parsed >= 0) {
            best = Math.max(best, parsed)
        }
    })
    return best
}

async function fetchImageDataUrl(url){
    if( !url || typeof fetch !== "function" ){
        return undefined
    }
    if( IMAGE_DATA_CACHE.has(url) ){
        return IMAGE_DATA_CACHE.get(url)
    }
    const pending = (async ()=>{
        try{
            const response = await fetch(url)
            if( !response.ok ){
                return undefined
            }
            const blob = await response.blob()
            return await new Promise((resolve)=>{
                const reader = new FileReader()
                reader.onloadend = ()=>resolve(typeof reader.result === "string" ? reader.result : undefined)
                reader.onerror = ()=>resolve(undefined)
                reader.readAsDataURL(blob)
            })
        }catch{
            return undefined
        }
    })()
    IMAGE_DATA_CACHE.set(url, pending)
    const result = await pending
    IMAGE_DATA_CACHE.set(url, result)
    return result
}

const FlowInstanceOutput = forwardRef(function FlowInstanceOutput({primitive, inputPrimitives, steps, hideEmptyPages = false, ...props},ref){
    const myState = useRef({})
    const canvas = useRef({})
    const canvasRenderSetRef = useRef([])
    const [shouldShowSpinner, setShouldShowSpinner] = useState(false)
    const hiddenPagesRef = useRef([])
    const hiddenPageIdsRef = useRef(new Set())
    // Evidence tooltip (provenance from VizDSL crystallizer)
    const evidenceTipRef = useRef(null)
    const evidenceTimerRef = useRef(null)


    async function exportToPptx(pptx){
        if( !canvas.current?.frameList || !canvas.current?.frameData ){
            return 0
        }
        const frames = canvas.current.frameList() 
        let exportedSlides = 0
        for(const id of frames){                    
            const root = canvas.current.frameData( id )
            if( !root?.node ){
                continue
            }
            const temp = root.node.children
            root.node.children = root.allNodes
            let pages = root.node.find("._page")

            if( pages.length > 0){
                for(const page of pages){
                    const background = root.node.find(".background")[0]
                    const backgroundColor = background?.attrs.fill
                    const childFrames = root.node.find(d=>d.attrs.pageTrack === page.attrs.pageIdx)
                    const aggNode = new Konva.Group({
                        width: page.width(),
                        height: page.height()
                    })
                    for(const child of childFrames){
                        child.ox = child.x()
                        child.oy = child.y()
                        child.x( child.ox - page.x() )
                        child.y( child.oy - page.y() )
                        child.oldParent = child.parent
                        aggNode.add( child )
                    }
                    await exportKonvaToPptx( aggNode, pptx, {removeNodes: IGNORE_NODES_FOR_EXPORT, noFraming: true, padding: [0,0,0,0], backgroundColor} )
                    for(const child of childFrames){
                        child.x( child.ox )
                        child.y( child.oy )
                        child.oldParent.add( child )
                        delete child["oldParent"]
                        delete child["ox"]
                        delete child["oy"]
                    }
                    exportedSlides++
                }
            }
            root.node.children = temp
        }
        return exportedSlides
    }
    async function waitForRenderStable(options = {}){
        const timeoutMs = Number.isFinite(Number(options.timeoutMs)) ? Number(options.timeoutMs) : 18000
        const settleMs = Number.isFinite(Number(options.settleMs)) ? Number(options.settleMs) : 600
        const started = Date.now()
        let stableSince = 0
        const isKonvaImageReady = (node)=>{
            const image = node?.image?.()
            if( !image ){
                return false
            }
            const complete = image.complete === true
            const naturalWidth = Number(image.naturalWidth ?? image.width ?? 0)
            return complete && naturalWidth > 0
        }
        const isCustomImageReady = (node)=>{
            if( !node ){
                return false
            }
            if( node.placeholder ){
                return false
            }
            const maxImage = node.maxImage
            const width = Number(maxImage?.width ?? 0)
            const height = Number(maxImage?.height ?? 0)
            if( width <= 0 || height <= 0 ){
                return false
            }
            if( node.queuedForRefresh ){
                return false
            }
            return true
        }
        while((Date.now() - started) < timeoutMs){
            const stage = canvas.current?.stageNode?.()
            const frameCount = canvas.current?.frameList?.()?.length ?? 0
            const imageNodes = stage?.find?.((node)=> node instanceof Konva.Image || node instanceof CustomImage) ?? []
            const pendingImageCount = imageNodes.filter((node)=>{
                if( node instanceof CustomImage ){
                    return !isCustomImageReady(node)
                }
                return !isKonvaImageReady(node)
            }).length
            const isStableNow = frameCount > 0 && pendingImageCount === 0
            if( isStableNow ){
                if( stableSince === 0 ){
                    stableSince = Date.now()
                }else if((Date.now() - stableSince) >= settleMs){
                    await new Promise((resolve)=>requestAnimationFrame(()=>resolve()))
                    await new Promise((resolve)=>requestAnimationFrame(()=>resolve()))
                    return true
                }
            }else{
                stableSince = 0
            }
            await new Promise((resolve)=>setTimeout(resolve, 50))
        }
        return false
    }
    async function addCoverSlide(pptx, options = {}){
        const { width, height } = getActiveLayoutInches(pptx)
        const marginX = width * 0.06
        const headerY = height * 0.33
        const dividerY = headerY + 3.05
        const titleY = dividerY + 0.45
        const contentWidth = width - (marginX * 2)
        const uniqueById = new Map()
        ;[primitive, ...(primitive?.primitives?.descendants ?? [])].filter(Boolean).forEach((item)=>{
            if( item?.id && !uniqueById.has(item.id) ){
                uniqueById.set(item.id, item)
            }
        })
        const uniqueFlowItems = Array.from(uniqueById.values())
        const totalSearchQueries = uniqueFlowItems.reduce(
            (sum, item) => sum + resolvePrimitiveSearchQueryCount(item),
            0
        )
        const totalUrlsExamined = uniqueFlowItems.reduce(
            (sum, item) => sum + resolvePrimitiveUrlExaminedCount(item),
            0
        )
        const evidenceGroups = Array.isArray(options.evidenceGroups)
            ? options.evidenceGroups
            : groupPrimitivesByType(primitive?.primitives?.descendants ?? [], OUTPUT_ENTITY_TYPES)
        const cappedEvidenceGroups = evidenceGroups.slice(0, 10)
        const evidenceLines = cappedEvidenceGroups.map((group)=>`${group.title}: ${group.count}`)
        if( evidenceGroups.length > cappedEvidenceGroups.length ){
            evidenceLines.push(`+${evidenceGroups.length - cappedEvidenceGroups.length} more`)
        }

        const slide = pptx.addSlide()
        slide.background = { color: "ECECEC" }

        const brandLogoW = 2.45
        const brandLogoH = 2.45
        const brandGap = 0.6
        const brandWordW = 12
        const brandWordH = 2.2
        const brandStartX = marginX
        const brandWordY = headerY + ((brandLogoH - brandWordH) / 2)

        const logoData = await fetchImageDataUrl(options.logoUrl ?? SENSE_LOGO_URL)
        if( logoData ){
            slide.addImage({
                data: logoData,
                x: brandStartX,
                y: headerY,
                w: brandLogoW,
                h: brandLogoH
            })
        }
        slide.addText("SENSE", {
            x: brandStartX + brandLogoW + brandGap,
            y: brandWordY,
            w: brandWordW,
            h: brandWordH,
            bold: true,
            fontSize: 160,
            color: "101B36",
            fontFace: "Poppins"
        })
        slide.addShape(pptx.shapes.LINE, {
            x: marginX,
            y: dividerY,
            w: contentWidth * 0.95,
            h: 0,
            line: {
                color: "111827",
                pt: 1.5
            }
        })

        const flowTitle = (primitive?.origin?.title ?? primitive?.origin?.referenceParameters?.title ?? "Flow").trim()
        const flowName = (options.flowName ?? primitive?.title ?? "Flow instance").trim()
        slide.addText(flowName, {
            x: marginX,
            y: titleY,
            w: contentWidth * 0.75,
            h: 1.45,
            bold: true,
            fontSize: 72,
            color: "101B36",
            fontFace: "Poppins"
        })
        slide.addText(flowTitle, {
            x: marginX,
            y: titleY + 1.0,
            w: contentWidth * 0.75,
            h: 0.95,
            fontSize: 41,
            italic: true,
            color: "36465E",
            fontFace: "Poppins"
        })

        slide.addText(`User: ${options.userName ?? resolveExportUserName(mainstore)}`, {
            x: marginX,
            y: titleY + 2.0,
            w: contentWidth * 0.48,
            h: 0.55,
            fontSize: 23,
            color: "2E3C52",
            fontFace: "Poppins"
        })
        slide.addText(`Date: ${options.dateLabel ?? formatCoverDate(new Date())}`, {
            x: marginX + (contentWidth * 0.62),
            y: titleY + 0.2,
            w: contentWidth * 0.33,
            h: 0.55,
            fontSize: 23,
            align: "right",
            color: "2E3C52",
            fontFace: "Poppins"
        })

        const lowerY = titleY + 5.6
        const evidenceColumnWidth = contentWidth * 0.5
        const dataColumnX = marginX + (contentWidth * 0.64)
        slide.addText("Evidence Types", {
            x: marginX,
            y: lowerY,
            w: evidenceColumnWidth,
            h: 0.8,
            bold: true,
            fontSize: 31,
            color: "7A7A7A",
            fontFace: "Poppins"
        })
        if( evidenceLines.length > 0 ){
            slide.addText(evidenceLines.join("\n"), {
                x: marginX,
                y: lowerY + 0.96,
                w: evidenceColumnWidth,
                h: Math.max(1.8, height - (lowerY + 2.0)),
                fontSize: 20.5,
                color: "6A6A6A",
                fontFace: "Poppins",
                breakLine: true,
                valign: "top"
            })
        }else{
            slide.addText("No evidence collected.", {
                x: marginX,
                y: lowerY + 0.96,
                w: evidenceColumnWidth,
                h: 0.4,
                fontSize: 20.5,
                color: "7A7A7A",
                fontFace: "Poppins"
            })
        }

        slide.addText("Data discovery", {
            x: dataColumnX,
            y: lowerY,
            w: contentWidth * 0.28,
            h: 0.8,
            bold: true,
            fontSize: 31,
            color: "7A7A7A",
            fontFace: "Poppins"
        })
        slide.addText(`Search queries: ${formatMetricNumber(totalSearchQueries)}`, {
            x: dataColumnX,
            y: lowerY + 1.0,
            w: contentWidth * 0.3,
            h: 0.6,
            fontSize: 22.5,
            color: "6A6A6A",
            fontFace: "Poppins"
        })
        slide.addText(`URLs inspected: ${formatMetricNumber(totalUrlsExamined)}`, {
            x: dataColumnX,
            y: lowerY + 1.75,
            w: contentWidth * 0.3,
            h: 0.6,
            fontSize: 22.5,
            color: "6A6A6A",
            fontFace: "Poppins"
        })

        const coCreatedLogo = await fetchImageDataUrl(CO_CREATED_LOGO_URL)
        if( coCreatedLogo ){
            slide.addImage({
                data: coCreatedLogo,
                x: width - marginX - 2.45,
                y: height - 2.7,
                w: 2.2,
                h: 2.2
            })
        }
    }
    async function downloadAll(titleOrOptions){
        const options = typeof titleOrOptions === "string"
            ? { title: titleOrOptions }
            : (titleOrOptions ?? {})
        const pptx = createPptx()
        await waitForRenderStable()
        if( options.includeCoverSlide ){
            await addCoverSlide(pptx, options.coverSlide ?? {})
        }
        await exportToPptx(pptx)
        const resolvedTitle = typeof options.title === "string" && options.title.trim().length ? options.title.trim() : "Sense Download"
        pptx.writeFile({ fileName: `${resolvedTitle}.pptx` });

    }
    useImperativeHandle(ref, () => {
        return {
            downloadAll,
            exportToPptx,
            waitForRenderStable,
            getHiddenPages: () => hiddenPagesRef.current ?? []
        };
      }, []);

    const cacheKey = `${primitive?.id ?? "none"}:${props.dataLoadVersion ?? 0}`
    if( myState.current?._cacheKey !== cacheKey ){
        myState.current = {_cacheKey: cacheKey}
        hiddenPageIdsRef.current = new Set()
        hiddenPagesRef.current = []
    }

    const state = myState.current
    state.renderSubPages = true
    state.hideWidgets = true

    const mainstore = MainStore()

    const outputNameFilter = useMemo(()=>{
        const names = (props.outputNames ?? []).map(name=>typeof name === "string" ? name.trim() : "").filter(Boolean)
        if( names.length === 0 ){
            return undefined
        }
        return new Set(names.map(name=>name.toLowerCase()))
    }, [props.outputNames])

    const flowOutputPins = primitive?.origin?.outputPins ?? []
    const outputPins = Object.keys(primitive?.primitives.outputs ?? {})
    const pagesOnly = props.pagesOnly === true
    const deferBuildWhileLoading = props.deferBuildWhileLoading === true
    const isDataLoading = props.isDataLoading === true
    const shouldDeferBoardBuild = deferBuildWhileLoading && isDataLoading
    const outputs = outputPins.reduce((acc,pinKey)=>{
        const [, pinInput] = pinKey.split("_")
        if( !flowOutputPins[pinInput]){
            return acc
        }
        const pinContent = primitive.outputs[pinInput]
        let items
        if( pinContent?.config === "primitive"){
            items = pinContent.data
        }
        let textEntries
        if( pinContent?.config === "string" ){
            const value = pinContent.data
            const normalized = value === undefined || value === null ? "" : (typeof value === "string" ? value : `${value}`)
            const trimmed = normalized.trim()
            textEntries = trimmed ? [trimmed] : []
        }else if( pinContent?.config === "string_list" ){
            const rawList = Array.isArray(pinContent.data) ? pinContent.data : [pinContent.data]
            textEntries = rawList
                .map(entry=>{
                    if( entry === undefined || entry === null ){
                        return null
                    }
                    const asString = typeof entry === "string" ? entry : `${entry}`
                    const trimmed = asString.trim()
                    return trimmed.length ? trimmed : null
                })
                .filter(Boolean)
        }
        const pinName = flowOutputPins[pinInput]?.name ?? pinInput
        const normalizedName = typeof pinName === "string" ? pinName.toLowerCase() : undefined
        if( outputNameFilter && (!normalizedName || !outputNameFilter.has(normalizedName)) ){
            return acc
        }
        const primitiveItems = Array.isArray(items)
            ? (pagesOnly ? items.filter((item)=>item?.type === "page") : items)
            : []
        const hasPrimitiveItems = primitiveItems.length > 0
        const hasTextEntries = Array.isArray(textEntries) && textEntries.length > 0
        if( !hasPrimitiveItems && (!hasTextEntries || pagesOnly) ){
            return acc
        }
        acc[pinKey] = {
            id: pinKey,
            name: pinName,
            items: primitiveItems,
            textEntries: pagesOnly ? [] : (hasTextEntries ? textEntries : []),
            config: pinContent?.config
        }
        return acc
    }, {})

    const watchedPrimitiveIds = useMemo(()=>{
        const normalizeId = (value)=>{
            if( value === undefined || value === null ){
                return undefined
            }
            return typeof value === "string" ? value : `${value}`
        }
        const idSet = new Set([primitive?.id, primitive?.origin?.id].map(normalizeId).filter(Boolean))
        Object.values(outputs ?? {}).forEach((entry)=>{
            const pinItems = Array.isArray(entry?.items) ? entry.items : []
            pinItems.forEach((item)=>{
                if( !item?.id ){
                    return
                }
                idSet.add(normalizeId(item.id))
                const directChildren = item?.primitives?.origin?.allIds ?? []
                directChildren.map(normalizeId).filter(Boolean).forEach((id)=>idSet.add(id))
                if( item.type === "page" ){
                    const configPageElementIds = item?.configParent?.primitives?.origin?.allIds ?? []
                    configPageElementIds.map(normalizeId).filter(Boolean).forEach((id)=>idSet.add(id))
                }
            })
        })
        return idSet
    }, [outputs, primitive?.id, primitive?.origin?.id])

    const handleDataEvent = useCallback((changedIds, eventName, fieldPath)=>{
        const normalizedIds = (Array.isArray(changedIds) ? changedIds : [])
            .map((id)=>id === undefined || id === null ? undefined : (typeof id === "string" ? id : `${id}`))
            .filter(Boolean)
        const isInstanceConfigEvent = eventName === "set_field" && typeof fieldPath === "string" && fieldPath.startsWith("instanceConfig.")
        if( normalizedIds.length > 0 && !isInstanceConfigEvent ){
            const hasRelevantChange = normalizedIds.some((id)=>watchedPrimitiveIds.has(id))
            if( !hasRelevantChange ){
                return false
            }
        }
        let shouldRefresh = false
        let shouldLightRefresh = false
        if( (eventName === "set_parameter" || eventName === "set_field") && typeof fieldPath === "string" ){
            shouldRefresh =
                fieldPath.startsWith("processing.flow") ||
                fieldPath.startsWith("processing.run_step") ||
                fieldPath.startsWith("outputs")
            shouldLightRefresh = fieldPath.startsWith("instanceConfig.")
            
            const processing = FLOW_ACTIVE_STATUSES.has(primitive?.processing?.flow?.status) || FLOW_ACTIVE_STATUSES.has(primitive?.origin?.processing?.flow?.status)
            const wantSpinner = processing && props.spinnerWhenRunning
            setShouldShowSpinner( wantSpinner )
                
        }else if(["relationship_update","set_parameter","new_child","delete_primitive","set_title"].includes(eventName)){
            shouldRefresh = true
        }        
        if( (shouldRefresh || shouldLightRefresh) && hideEmptyPages ){
            hiddenPageIdsRef.current = new Set()
            hiddenPagesRef.current = []
        }
        if( shouldLightRefresh && !shouldRefresh ){
            requestAnimationFrame(()=>{
                const renderEntries = canvasRenderSetRef.current ?? []
                const renderById = new Map(renderEntries.map((entry)=>[entry?.id, entry]))
                const changedIdSet = new Set(normalizedIds)
                const targetFrameIds = new Set()
                const changedOriginIds = new Set()
                for(const changedId of changedIdSet){
                    const changedPrimitive = mainstore.primitive(changedId)
                    const changedOriginId = changedPrimitive?.origin?.id
                    if( changedOriginId ){
                        changedOriginIds.add(changedOriginId)
                    }
                }
                for(const entry of renderEntries){
                    const frameId = entry?.id
                    if( !frameId ){
                        continue
                    }
                    const framePrimitive = entry?.primitive ?? mainstore.primitive(frameId)
                    const frameConfigParentId = framePrimitive?.configParent?.id
                    const frameState = state?.[frameId] ?? {}
                    const isImpacted =
                        changedIdSet.has(frameId) ||
                        changedIdSet.has(frameState?.underlying?.id) ||
                        (frameConfigParentId && changedOriginIds.has(frameConfigParentId)) ||
                        (Array.isArray(frameState?.internalWatchIds) && frameState.internalWatchIds.some((id)=>changedIdSet.has(`${id}`)))
                    if( isImpacted ){
                        targetFrameIds.add(frameId)
                    }
                }
                for(const frameId of targetFrameIds){
                    const nextRender = renderById.get(frameId)
                    if( nextRender && canvas.current?.refreshFrame ){
                        canvas.current.refreshFrame(frameId, nextRender)
                    }
                }
            })
            return false
        }
    }, [hideEmptyPages, primitive?.origin?.processing?.flow?.status, primitive?.processing?.flow?.status, props.spinnerWhenRunning, watchedPrimitiveIds])

    useDataEvent("relationship_update set_parameter set_field delete_primitive set_title new_child", undefined, handleDataEvent)
    
    const flow = primitive.findParentPrimitives({type:"flow"})[0]


    const allRenderedSet = useMemo(()=>{
        if( shouldDeferBoardBuild ){
            hiddenPagesRef.current = []
            hiddenPageIdsRef.current = new Set()
            return []
        }
        hiddenPagesRef.current = []
        let order = 0
        const prepare = Object.keys(outputs ?? {}).flatMap(pin=>{
            const pinEntry = outputs[pin] ?? {}
            const pinItems = Array.isArray(pinEntry.items) ? pinEntry.items : []
            const textEntries = Array.isArray(pinEntry.textEntries) ? pinEntry.textEntries : []
            const inputPin = pin.split("_")[1]
            const pinTitle = flow?.referenceParameters?.outputPins?.[inputPin]?.name ?? pinEntry.name ?? `Output for ${pin}`
            const pinResults = []

            for(const d of pinItems){
                if( !d ){
                    continue
                }
                order++
                state[d.id] ||= { id: d.id }
                state[d.id].renderSubPages = true
                state[d.id]._order = order
                state[d.id].title = `Slide `
                const themeKey = d.renderConfig?.theme  ?? d.configParent?.renderConfig?.theme ?? "default";
                const theme = themes[themeKey] || themes.default;

                if( d.type === "view" ){
                    const viewRenderInput = buildViewRenderInput({ primitive: d, lockDimensions: false })
                    if( !viewRenderInput ){
                        continue
                    }
                    const fallbackTitle = pinTitle ?? d.title ?? d.referenceParameters?.title ?? "View output"
                    const frameId = d.id ?? `${pin}_view_${order}`
                    pinResults.push({
                        id: frameId,
                        frameless: true,
                        ringless: true,
                        canChangeSize: true,
                        canvasMargin: [24,24,24,24],
                        theme,
                        title: () => fallbackTitle,
                        items: stageOptions =>
                            renderDatatable({
                                ...viewRenderInput,
                                stageOptions,
                                renderOptions: {
                                    ...viewRenderInput.renderOptions,
                                },
                                theme,
                            })
                    })
                    continue
                }

                BoardViewer.prepareBoard(d, state)
                const renderedContent = BoardViewer.renderBoardView(d, primitive, state, {theme})

                if( hideEmptyPages && d.type === "page" && typeof renderedContent?.items === "function" ){
                    const baseItems = renderedContent.items
                    renderedContent.items = (stageOptions) => {
                        const node = baseItems(stageOptions)
                        if( !node ){
                            return node
                        }
                        const pageGroups = typeof node.find === "function" ? node.find("._page") : []
                        const emptyGroups = pageGroups.filter(group => group?.attrs?.emptyContent)
                        let emptyElementIds = []
                        let hasEmptyContent = false
                        if( emptyGroups.length > 0 ){
                            hasEmptyContent = true
                            emptyElementIds = emptyGroups
                                .map(group => Array.isArray(group?.attrs?.emptyElementIds) ? group.attrs.emptyElementIds : [])
                                .flat()
                                .filter(Boolean)
                        }else if( node?.attrs?.emptyContent ){
                            hasEmptyContent = true
                            emptyElementIds = Array.isArray(node?.attrs?.emptyElementIds) ? node.attrs.emptyElementIds : []
                        }
                        if( hasEmptyContent ){
                            node.attrs.abortFrame = true
                            if( !hiddenPageIdsRef.current.has(d.id) ){
                                hiddenPageIdsRef.current.add(d.id)
                                hiddenPagesRef.current.push({
                                    primitiveId: d.id,
                                    title: d.title ?? d.name ?? d.referenceParameters?.title ?? `Page ${d.plainId ?? d.id}`,
                                    pinKey: pin,
                                    pinName: pinTitle,
                                    emptyElementIds
                                })
                            }
                        }
                        return node
                    }
                }
                pinResults.push(renderedContent)
            }

            if( textEntries.length > 0 ){
                order++
                const frameId = `${pin}_text_${order}`
                const normalizedText = textEntries.map(entry => typeof entry === "string" ? entry : `${entry}`)
                const renderType = "text"
                const renderText =normalizedText.join("\n")
                state[frameId] = { id: frameId, renderSubPages: false, _order: order, textOutput: true }
                state[frameId].title = `Slide `
                const textThemeKey = primitive.renderConfig?.theme ?? "default"
                const textTheme = themes[textThemeKey] || themes.default
                const lineCount = normalizedText.reduce((count, entry)=>count + entry.split(/\r?\n/).length, 0)
                const estimatedHeight = Math.min(5200, Math.max(320, lineCount * 32))
                pinResults.push({
                    id: frameId,
                    frameless: true,
                    ringless: true,
                    canChangeSize: true,
                    canvasMargin: [24,24,24,24],
                    theme: textTheme,
                    title: () => pinTitle,
                    items: stageOptions =>
                        renderPlainObject({
                            id: frameId,
                            type: renderType,
                            text: renderText,
                            width: stageOptions?.width ?? 960,
                            height: stageOptions?.height ?? estimatedHeight,
                            padding: [24,24,24,24],
                            theme: textTheme,
                            ...stageOptions,
                        })
                })
            }

            return pinResults.filter(Boolean)
        }).filter(Boolean)

        return prepare
    }, [primitive?.id, shouldShowSpinner, outputs, flow?.referenceParameters?.outputPins, hideEmptyPages, props.dataLoadVersion, shouldDeferBoardBuild])
    
    const resolvedFrameIdx = props.frameIdx !== undefined
        ? Math.max(0, Math.min(props.frameIdx, allRenderedSet.length - 1))
        : undefined
    const renderedSet = (resolvedFrameIdx !== undefined ? [allRenderedSet.at(resolvedFrameIdx)] : allRenderedSet).filter(Boolean)
    
    const canvasRenderSet = useMemo(()=>{
        const spinnerMessage = props.spinnerMessage ?? "Flow is running...";
        if( !shouldShowSpinner ){
            return renderedSet
        }
        const fallbackTitle = primitive?.title ?? primitive?.name ?? (primitive?.plainId ? `Flow ${primitive.plainId}` : "Workflow output")
        const baseSet = renderedSet.length > 0 ? renderedSet : [{
            id: `${primitive?.id ?? "flow"}_spinner`,
            frameless: true,
            ringless: true,
            title: () => fallbackTitle,
            canvasMargin: [48,48,48,48],
            canChangeSize: true
        }]
        return baseSet.map(view=>{
            const frameConfig = primitive?.frames?.[view.id]
            const placeholderWidth = Number.isFinite(frameConfig?.width) ? frameConfig.width : 960
            const placeholderHeight = Number.isFinite(frameConfig?.height) ? frameConfig.height : 540
            return {
                ...view,
                canChangeSize: true,
                items: stageOptions => renderProcessingPlaceholder(stageOptions, {
                    width: placeholderWidth,
                    height: placeholderHeight,
                    theme: view.theme ?? themes.default,
                    animationId: `${view.id}_processing`,
                    messageText: spinnerMessage
                })
            }
        })
    }, [shouldShowSpinner, renderedSet, primitive?.frames, primitive?.id, primitive?.plainId, primitive?.title, primitive?.name, props.spinnerMessage])

    useEffect(()=>{
        canvasRenderSetRef.current = canvasRenderSet
    }, [canvasRenderSet])

    useEffect(()=>{
        if( shouldShowSpinner ){
            return
        }
        if( props.frameIdx !== undefined){
            if( canvas.current?.frameList ){
                const list = canvas.current.frameList()
                for(const d of list){
                    canvas.current.removeFrame(d)
                }
                canvas.current.addFrame( renderedSet[0] )
                if( props.onSlideChange ){

                    const pageState = state[renderedSet[0].id]
                    const subpageState = pageState?.subpages?.[renderedSet[0].id]
                    const sourceData = Object.values(subpageState?.pageInputs ?? {}).flatMap(d=>d?.data).filter(Boolean)
                    /*const sourceData = directData.flatMap(d=>{
                        switch( d.type ){
                            case "result": return d
                            case "summary": return d.primitives.source.allItems
                            case "entity": return uniquePrimitives(d.primitives.strictDescendants.filter(d=>d.type==="result"))
                        }
                    })*/
                    props.onSlideChange( {item: renderedSet[0], sourceData} )
                }
            }
        }

    }, [resolvedFrameIdx, renderedSet?.length, canvas.current, shouldShowSpinner])

    useEffect(()=>{
        if( shouldShowSpinner ){
            return
        }
        if( props.highlightIdx !== undefined ){
            setTimeout(()=>{
                if( renderedSet && canvas.current?.selectFrame ){
                    canvas.current.selectFrame( renderedSet[props.highlightIdx]?.id)
                }
            },100)
        }
    }, [props.highlightIdx, renderedSet?.length, canvas.current?.selectFrame, shouldShowSpinner])
    useEffect(()=>{
        if( shouldShowSpinner ){
            return
        }
        const timer = setTimeout(()=>{
            const ids = canvas.current?.frameList?.() ?? []
            if( ids.length === 0 ){
                return
            }
            ids.forEach((id, idx)=>{
                const frame = canvas.current?.frameData?.(id)
                const label = frame?.label
                const titleNode = label?.titleNode
                if( !titleNode ){
                    return
                }
                const nextText = `Slide ${idx + 1}`
                if( titleNode.text() === nextText ){
                    return
                }
                titleNode.text(nextText)
                const titleWidth = titleNode.width()
                const titleHeight = titleNode.height()
                label.attrs.originWidth = titleWidth
                label.attrs.baseHeight = titleHeight
                label.attrs.baseOffsetY = titleHeight
                label.offsetY(titleHeight)
                label.width(titleWidth)
                label.height(titleHeight)
                if( label.labelBg ){
                    label.labelBg.width(titleWidth)
                    label.labelBg.height(titleHeight)
                }
                titleNode.getLayer()?.batchDraw()
            })
        }, 0)
        return ()=>clearTimeout(timer)
    }, [renderedSet?.length, shouldShowSpinner, hideEmptyPages])
    const selectItems = useCallback((...args) => {
        if( props.select){
            props.select(...args)
        }else{
            mainstore.sidebarSelect(...args)
        }
    }, [mainstore, props.select])

    const resolveRenderContext = useCallback((renderContextId) => {
        if (!renderContextId) {
            return undefined;
        }
        const pageId = renderedSet?.[0]?.id;
        if (!pageId) {
            return undefined;
        }
        const map = state?.[pageId]?.subpages?.[pageId]?.renderContexts;
        return map?.get ? map.get(renderContextId) : undefined;
    }, [renderedSet, state]);

    const sharedCallbacks = useMemo(() => {
        return buildCanvasClickHandlers({
            mainstore,
            selectItems,
            uniquePrimitives,
            resolveRenderContext,
        });
    }, [mainstore, selectItems, resolveRenderContext]);

    if( !primitive ){
        return <></>
    }

    if( canvasRenderSet.length == 0){
        return <></>
    }        
    
    //return  <div className="flex h-full w-full relative border rounded-lg border-gray-200 overflow-hidden mb-2 bg-white">
    return  <div className="@container flex h-full w-full flex-1 min-h-0 relative overflow-hidden">
                <InfiniteCanvas
                    key={`${shouldShowSpinner}-${hideEmptyPages}-${(props.outputNames ?? []).join("-")}`}
                    initialZoom={props.initialZoom}
                    primitive={primitive}
                    board
                    hideWidgets={true}
                    ref={canvas}
                    background={props.background ?? "#fefefe"}
                    ignoreAfterDrag={false}
                    singleFrame={props.singleFrame}
                    accent={props.accent}
                    layout={props.layout}
                    enableZoom={props.enableZoom}
                    enablePan={props.enablePan}
                    enableDrag={props.enableDrag}
                    disableHover={props.disableHover}
                    enableTextSelection={props.enableTextSelection}
                    clearTextSelectionKey={props.frameIdx}
                    showPins={false}
                    events={{
                        wheel: {
                            passive: false
                        }
                    }}
                    highlights={props.clickToSelect ? {} : {
                        "primitive":"border",
                        "cell":"background",
                        "widget":"background",
                        "viz_block":"border"
                    }}
                    enableShapeSelection={false}
                    enableFrameSelection={props.enableFrameSelection ?? true}
                    callbacks={props.clickToSelect ? {
                        onClick:{
                            frame: (id)=>{
                                const idx = allRenderedSet.findIndex(d=>d.id === id)
                                if( idx > -1 ){
                                    props.clickToSelect(idx)
                                }
                            }
                        }
                        } : sharedCallbacks}
                    selectable={props.clickToSelect ? {} : {
                        "frame":{
                            multiple: false
                        },
                        "primitive":{
                            multiple: false
                        },
                        "cell":{
                            multiple: true
                        }
                    }}
                    render={canvasRenderSet}/>
            </div>
})
export default FlowInstanceOutput
